/// <amd-module name="Wiki/NoWiki/NoWiki" />
import * as React from "react";

import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssUserClaimsService, UserClaims } from "VSS/Platform/UserClaims";
import { IZeroDataItem, ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import { ITfsPageService } from "Tfs/Platform/Page";
import * as VC_Constants from "Wiki/Clients/VC/Contracts/Constants";
import * as Resources from "Wiki/NoWiki/Resources";
import { WikiNoPermission } from "Wiki/NoWiki/WikiNoPermission";
import { WikiZeroDay } from "Wiki/NoWiki/WikiZeroDay";
import { getWikiHomePageUrl } from "Wiki/ViewCommon/Utils/WikiHelper";
import * as WikiPermissionsHelper from "Wiki/ViewCommon/Utils/WikiPermissionsHelper";
import { LoadingSpinner } from "Wiki/ViewCommon/WrappedDialogHelper";

export interface NoWikiProps {
    isStakeHolder: boolean;
    hasDefaultWikiAccess: boolean;
    permissionError: string | undefined;
    isTfvcOnlyProject: boolean;
    isProjectWikiExisting: boolean;
    isUserGivenWiki: boolean;
    isIllegalPath: boolean;
    isRepositoryDisabled: boolean;
}

interface NoPermissionOnAnyWikiState {
    showWikiSecurityLoadingSpinner?: boolean;
}

interface NoPermissionOnAnyWikiProps {
    showProjectWikiSecurity?: boolean;
    projectId: string;
}

interface ProjectWikiAdminPayload {
    wikiRepoId: string;
}

export class NoWiki extends VssComponent<NoWikiProps> {
    private _projectId: string | undefined;
    private _hasWikiCreatePermission: boolean;
    private _hasProjectContributePermission: boolean;

    constructor(props: NoWikiProps, context: IVssComponentContext) {
        super(props, context);
        const pageData = this.context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        this._projectId = pageData && pageData.project && pageData.project.id;

        this._hasWikiCreatePermission = this._projectId
            ? WikiPermissionsHelper.hasWikiCreatePermission(this.context.pageContext, this._projectId, this.props.isStakeHolder)
            : false;
        this._hasProjectContributePermission = this._projectId
            ? WikiPermissionsHelper.hasProjectContributePermission(this.context.pageContext, this._projectId, this.props.isStakeHolder)
            : false;
    }

    public render(): JSX.Element | null {

        if (this.props.isIllegalPath) {
            return (
                <div className="wiki-nowiki flex-column justify-center">
                    <ZeroData
                        primaryText={Resources.WikiIllegalPath_PrimaryText}
                        secondaryText={Resources.WikiIllegalPath_SecondaryText}
                        imagePath={getAssetLocation(this.context.pageContext, "ms.vss-wiki-web/wiki-overview-content/Images/page-not-found.svg")}
                        imageAltText=""
                    />
                </div>
            );
        }

        // always show "No permission on this wiki" for stakeholders
        // as stakeholder permission is not controlled via git permissioning
        if (this.props.isStakeHolder) {
            return <WikiNoPermission primaryText={Resources.NoPermissions_OnThisWiki_PrimaryText} />;
        }
        // TODO: show wiki security if project wiki exists
        if (this._projectId) {
            // this happens when user has requested for a wiki and he has no permission
            if (this.props.permissionError) {
                // he has another wiki that he can access
                if (this.props.hasDefaultWikiAccess) {
                    return (
                        <WikiNoPermission
                            primaryText={Resources.NoPermissions_OnThisWiki_PrimaryText}
                            actionType={ZeroDataActionType.link}
                            actionHref={getWikiHomePageUrl(this.context.pageContext, this._projectId)}
                            actionText={Resources.NoPermissions_OnThisWiki_LinkText}
                        />
                    );
                }
                // no accessible wiki exists
                return this._renderNoPermissionOnAnyWiki(this._projectId);
            }
            if (this.props.isUserGivenWiki) {
               
                if (this.props.isRepositoryDisabled) {
                    return (
                        <div className="wiki-nowiki flex-column justify-center">
                            <ZeroData
                                primaryText={Resources.RepositoryDisabled_PrimaryText}
                                secondaryText={Resources.RepositoryDisabled_SecondaryText}
                                imagePath={getAssetLocation(this.context.pageContext, "ms.vss-wiki-web/wiki-overview-content/Images/page-not-found.svg")}
                                imageAltText=""
                            />
                        </div>
                    );
                }
                // If the landing wiki is null and user has given a wiki name, then show him 404
                return (
                    <div className="wiki-nowiki flex-column justify-center">
                        <ZeroData
                            primaryText={Resources.WikiNotExists_PrimaryText}
                            secondaryText={Resources.WikiNotExists_SecondaryText}
                            imagePath={getAssetLocation(this.context.pageContext, "ms.vss-wiki-web/wiki-overview-content/Images/page-not-found.svg")}
                            imageAltText=""
                        />
                    </div>
                );
            }
            if (this.props.isProjectWikiExisting) {
                // this happens when user has not asked for any wiki (like clicking hub group)
                // and he has no accessible wiki, rather than showing day-0 show no permission screen
                // however currently there is no property that tells whether code wiki exists
                // for now just see if project wiki exists
                return this._renderNoPermissionOnAnyWiki(this._projectId);                
            }

            // Incase of TFVC projects always show create wiki
            // don't rely on git permissions
            if (this.props.isTfvcOnlyProject) {
                return <WikiZeroDay hasWikiCreatePermission={true} isTFVCOnlyProject={true} projectId={this._projectId} />;
            }

            if (!this._hasWikiCreatePermission && !this._hasProjectContributePermission) {
                // If user has no permission to create/publish a wiki, show no permission page
                return this._renderNoPermissionOnAnyWiki(this._projectId);
            }

            return (
                <WikiZeroDay
                    hasWikiCreatePermission={this._hasWikiCreatePermission}
                    hasProjectContributePermission={this._hasProjectContributePermission}
                    projectId={this._projectId}
                />
            );
        }
        // unexpected
        return null;
    }

    private _renderNoPermissionOnAnyWiki(projectId: string): JSX.Element {
        const showProjectWikiSecurity =
            this.props.isProjectWikiExisting &&
            this.context.pageContext.getService<IVssUserClaimsService>("IVssUserClaimsService").hasClaim(UserClaims.Member);
        return <NoPermissionOnAnyWiki projectId={projectId} showProjectWikiSecurity={showProjectWikiSecurity} />;
    }
}

class NoPermissionOnAnyWiki extends VssComponent<NoPermissionOnAnyWikiProps, NoPermissionOnAnyWikiState> {
    constructor(props: NoPermissionOnAnyWikiProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
    }

    public render(): JSX.Element {
        return (
            <>
                <WikiNoPermission
                    primaryText={Resources.NoPermissions_OnAnyWiki_PrimaryText}
                    onActionClick={this.props.showProjectWikiSecurity ? this._showWikiSecurity : undefined}
                    actionText={this.props.showProjectWikiSecurity ? Resources.NoPermissions_WikiSecurityTitle : undefined}
                    actionType={this.props.showProjectWikiSecurity ? ZeroDataActionType.ctaButton : undefined}
                />
                {this.state.showWikiSecurityLoadingSpinner && <LoadingSpinner />}
            </>
        );
    }

    private _showWikiSecurity = (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item?: IZeroDataItem): void => {
        this.setState({
            showWikiSecurityLoadingSpinner: true
        });

        const projectId = this.props.projectId;
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const wikiPagesDataProviderDataPromise = this.trackPromise(
            contributionService.getDataAsync<ProjectWikiAdminPayload>("ms.vss-wiki-web.wiki-admin-security-data-provider")
        ).promise;

        wikiPagesDataProviderDataPromise.then((data: ProjectWikiAdminPayload) => {
            const legacyPlatformService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
            legacyPlatformService.requireModules(["Admin/Scripts/TFS.Admin.Security"]).then(([tfsAdminSecurity]) => {
                this.setState({
                    showWikiSecurityLoadingSpinner: false
                });
                const repoSecurityManager = new tfsAdminSecurity.SecurityManager.create(VC_Constants.GitConstants.GitSecurityNamespaceId, {
                    projectGuid: projectId
                });
                const repoSecurityToken = WikiPermissionsHelper.calculateGitSecuredToken(projectId, data.wikiRepoId);

                repoSecurityManager.showPermissions(
                    repoSecurityToken,
                    Resources.NoPermissions_WikiSecurityTitle,
                    Resources.NoPermissions_WikiSecurityTitle,
                    undefined,
                    "900"
                );
            });
        });
    };
}

VssComponent.register("Wiki.NoWiki", NoWiki);
